import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { Alert } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { RegisterCtaLink } from '../../components/layout/RegisterCta';
import { Input, Select } from '../../components/ui/Input';
import { GeneratePasswordAction } from '../../components/ui/GeneratePasswordAction';
import { useAuth } from '../../context/AuthContext';
import { formatDateTime } from '../../utils/format';
import { storeLoginCredential } from '../../utils/credentials';

export function AccountPage() {
  const { user, refreshUser, deleteAccount, isDemo } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [representationType, setRepresentationType] = useState<'fisica' | 'juridica'>('fisica');
  const [representativeNif, setRepresentativeNif] = useState('');
  const [representationBusy, setRepresentationBusy] = useState(false);
  const [representationMessage, setRepresentationMessage] = useState('');
  const [representationError, setRepresentationError] = useState('');

  useEffect(() => {
    setRepresentationType(user?.representationEntityType ?? 'fisica');
    setRepresentativeNif(user?.representativeNif ?? '');
  }, [user?.representationEntityType, user?.representativeNif]);

  async function handleRepresentationSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isDemo) return;
    setRepresentationBusy(true);
    setRepresentationMessage('');
    setRepresentationError('');
    try {
      const result = await api.patch<{ message: string }>('/api/account/representation', {
        representationEntityType: representationType,
        representativeNif,
      });
      setRepresentationMessage(result.message);
      await refreshUser();
    } catch (err) {
      setRepresentationError(
        err instanceof Error ? err.message : 'No se pudieron guardar los datos de representación'
      );
    } finally {
      setRepresentationBusy(false);
    }
  }

  async function handleChangePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    const formData = new FormData(event.currentTarget);
    const submittedCurrent = String(formData.get('current-password') ?? currentPassword);
    const submittedNew = String(formData.get('new-password') ?? newPassword);

    try {
      const result = await api.post<{ message: string }>('/api/account/change-password', {
        currentPassword: submittedCurrent,
        newPassword: submittedNew,
      });
      setMessage(result.message);
      setCurrentPassword('');
      setNewPassword('');
      if (user?.email) {
        await storeLoginCredential(user.email, submittedNew);
      }
      await refreshUser();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isDemo) return;

    if (!deletePassword) {
      setDeleteError('Introduce tu contraseña para confirmar la eliminación.');
      return;
    }

    const confirmed = window.confirm(
      'Esta acción es permanente e irreversible. Se cancelarán tus suscripciones activas y se eliminarán tus datos de cuenta, licencias e informes asociados. ¿Deseas continuar?'
    );
    if (!confirmed) return;

    setDeleting(true);
    setDeleteError('');
    try {
      await deleteAccount(deletePassword);
      navigate('/login?deleted=1');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'No se pudo eliminar la cuenta');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <div className="panel-page-header">
        <div>
          <h1 className="page-title">Mi cuenta</h1>
          <p className="page-subtitle">Datos de acceso y seguridad.</p>
        </div>
      </div>

      <div className="grid-2">
        <Card title="Perfil">
          <dl className="stack muted">
            <div>
              <dt>Nombre</dt>
              <dd>
                <strong style={{ color: 'var(--color-text)' }}>
                  {user?.name} {user?.surname}
                </strong>
              </dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>
                <strong style={{ color: 'var(--color-text)' }}>{user?.email}</strong>
              </dd>
            </div>
            <div>
              <dt>Estado</dt>
              <dd>{user?.status ?? 'activa'}</dd>
            </div>
            {user?.createdAt ? (
              <div>
                <dt>Alta</dt>
                <dd>{formatDateTime(user.createdAt)}</dd>
              </div>
            ) : null}
          </dl>
        </Card>

        <Card title="Seguridad">
          {isDemo ? (
            <p className="muted">
              La contraseña de la demo es compartida y no se puede cambiar.{' '}
              <RegisterCtaLink>Crea tu cuenta</RegisterCtaLink> para gestionar tus datos de acceso.
            </p>
          ) : (
            <>
              {message ? <Alert tone="success">{message}</Alert> : null}
              {error ? <Alert tone="danger">{error}</Alert> : null}
              <form
                method="post"
                action="/panel/cuenta"
                autoComplete="on"
                onSubmit={handleChangePassword}
                className="stack"
              >
                <Input
                  label="Contraseña actual"
                  id="current-password"
                  name="current-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
                <Input
                  label="Contraseña nueva"
                  id="account-new-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  showPasswordToggle
                  labelAction={
                    <GeneratePasswordAction disabled={loading} onGenerate={setNewPassword} />
                  }
                />
                <Button type="submit" loading={loading}>
                  Cambiar contraseña
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>

      <div style={{ marginTop: '1.5rem', maxWidth: '720px' }}>
        <Card title="Datos fiscales y representación">
          <p className="muted" style={{ marginTop: 0 }}>
            Identifican al titular o representante legal que firmará el modelo de representación de
            Verifactu. La razón social y el NIF de la empresa se configuran por separado en cada licencia.
          </p>
          {isDemo ? (
            <p className="muted">Estos datos no se pueden modificar en la cuenta demo.</p>
          ) : (
            <form className="stack" onSubmit={handleRepresentationSubmit}>
              {representationMessage ? <Alert tone="success">{representationMessage}</Alert> : null}
              {representationError ? <Alert tone="danger">{representationError}</Alert> : null}
              <Select
                label="Tipo de titular fiscal"
                value={representationType}
                onChange={(event) =>
                  setRepresentationType(event.target.value as 'fisica' | 'juridica')
                }
                options={[
                  { value: 'fisica', label: 'Persona física / autónomo' },
                  { value: 'juridica', label: 'Persona jurídica / sociedad' },
                ]}
              />
              <Input
                label={
                  representationType === 'juridica'
                    ? 'NIF del representante legal'
                    : 'NIF del titular'
                }
                value={representativeNif}
                onChange={(event) => setRepresentativeNif(event.target.value.toUpperCase())}
                maxLength={20}
                required
                hint={`El firmante será ${user?.name ?? ''} ${user?.surname ?? ''}.`}
              />
              <Button type="submit" loading={representationBusy}>
                Guardar datos de representación
              </Button>
            </form>
          )}
        </Card>
      </div>

      <div style={{ marginTop: '1.5rem', maxWidth: '720px' }}>
        <Card title="Eliminar cuenta">
          {isDemo ? (
            <p className="muted">
              En modo demo no puedes eliminar la cuenta.{' '}
              <RegisterCtaLink>Crea tu cuenta</RegisterCtaLink> para gestionarla.
            </p>
          ) : (
            <>
              <p className="muted" style={{ marginBottom: '1rem' }}>
                Puedes eliminar tu cuenta de forma permanente. Se cancelarán las suscripciones activas
                y se borrarán tus datos personales e informes asociados.
              </p>
              {deleteError ? <Alert tone="danger">{deleteError}</Alert> : null}
              <form
                method="post"
                action="/panel/cuenta"
                autoComplete="on"
                onSubmit={handleDeleteAccount}
                className="stack"
              >
                <Input
                  label="Contraseña"
                  id="delete-account-password"
                  name="delete-account-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  disabled={deleting}
                />
                <Button type="submit" variant="danger" loading={deleting}>
                  Eliminar mi cuenta
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
